/**
 * CDM Users — list + drill-in for the persons / users / identities split.
 *
 * Phase 1 surfaces the data we have today (mostly empty until Entra
 * adapter pulls land). Phase 7 enriches via HR feed (employment_status,
 * department, manager).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Users, RefreshCw, ChevronLeft, Search, Filter,
  Shield, Bot, AlertTriangle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface CdmUser {
  user_id: string
  person_id: string | null
  kind: string
  upn: string | null
  sam: string | null
  state: string
  person_name: string | null
  department: string | null
  employment_status: string | null
}

const KIND_TONE: Record<string, string> = {
  human_primary:      'bg-blue/10 text-blue-light border-blue/30',
  human_admin:        'bg-rose/10 text-rose-light border-rose/30',
  human_token:        'bg-amber/10 text-amber-light border-amber/30',
  service_account:    'bg-purple/10 text-purple-light border-purple/30',
  workload_identity:  'bg-cyan/10 text-cyan border-cyan/30',
  system_account:     'bg-text-muted/10 text-text-muted border-border',
}

const STATE_TONE: Record<string, string> = {
  active:     'text-emerald',
  disabled:   'text-text-muted',
  orphaned:   'text-amber-light',
  quarantined: 'text-rose-light',
  deleted:    'text-text-muted',
}

export default function CdmUsersPage() {
  const [users, setUsers] = useState<CdmUser[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [kindFilter, setKindFilter] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/users', { params: { limit: 200, kind: kindFilter || undefined } })
      .then(r => setUsers(r.data?.users || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [kindFilter])

  const kinds = useMemo(() => {
    const s = new Set(users.map(u => u.kind))
    return Array.from(s).sort()
  }, [users])

  const filtered = useMemo(() =>
    users.filter(u => !search ||
      u.upn?.toLowerCase().includes(search.toLowerCase()) ||
      u.person_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.department?.toLowerCase().includes(search.toLowerCase())),
  [users, search])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Users size={18} className="text-blue-light" /> CDM USERS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          persons · accounts · identities · NHIs
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={kindFilter} onChange={e => setKindFilter(e.target.value)}
                aria-label="Filter by kind">
          <option value="">All kinds</option>
          {kinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="search by upn / name / department"
            className="input pl-7 w-full" />
        </div>
        <span className="text-text-muted ml-auto">{filtered.length} of {users.length}</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">UPN</th>
              <th className="text-left px-3 py-2">Person</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Department</th>
              <th className="text-left px-3 py-2">Employment</th>
              <th className="text-left px-3 py-2">State</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">
                No users yet. Configure the Entra CDM adapter or the existing Entra
                connector under /app/connectors-manage and trigger a pull.
              </td></tr>
            )}
            {filtered.map(u => {
              const Icon = u.kind.includes('service') || u.kind.includes('workload') ? Bot : Shield
              return (
                <tr key={u.user_id} className="hover:bg-surface-raised/40">
                  <td className="px-3 py-1.5">
                    <Link to={`/app/cdm/users/${u.user_id}`}
                          className="text-blue-light hover:underline font-mono text-[11px]">
                      {u.upn || '—'}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5">{u.person_name || <span className="text-text-muted">(no person)</span>}</td>
                  <td className="px-3 py-1.5">
                    <span className={clsx('badge text-[9px] inline-flex items-center gap-1 border',
                                         KIND_TONE[u.kind] || 'bg-surface-raised text-text-muted border-border')}>
                      <Icon size={10} /> {u.kind}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">{u.department || '—'}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{u.employment_status || '—'}</td>
                  <td className={clsx('px-3 py-1.5 font-mono text-[11px]', STATE_TONE[u.state] || 'text-text-muted')}>
                    {u.state}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
